/**
 * Organization membership schemas for validation
 */

import { z } from 'zod';
import { userResponseSchema } from './user.schema.js';
import { organizationResponseSchema } from './organization.schema.js';
import { RoleKeySchema } from './roles.js';

/**
 * A membership's role is a ROLE KEY into the one roles table (c24), not a
 * closed four-value enum.
 *
 * Foundations 1 / B1-T1: the enum that stood here was one of five stores for
 * the same noun, and it could not name the fifth shipped role (`power_user`)
 * or any tenant role at all. Roles are DATA now; the type source is
 * `RoleKeySchema` in ./roles.ts.
 */

/** Add member to organization request */
export const addMemberSchema = z.object({
  userId: z.string().uuid(),
  role: RoleKeySchema.default('member'),
});
export type AddMemberInput = z.infer<typeof addMemberSchema>;

/** Update member role request */
export const updateMemberRoleSchema = z.object({
  role: RoleKeySchema,
});
export type UpdateMemberRoleInput = z.infer<typeof updateMemberRoleSchema>;

/** Organization member response */
export const memberResponseSchema = z.object({
  userId: z.string().uuid(),
  organizationId: z.string().uuid(),
  role: RoleKeySchema,
  joinedAt: z.string().datetime(),
  invitedBy: z.string().uuid().nullable(),
  user: userResponseSchema.optional(),
});
export type MemberResponse = z.infer<typeof memberResponseSchema>;

/** Member list response */
export const memberListResponseSchema = z.object({
  data: z.array(memberResponseSchema),
  pagination: z.object({
    page: z.number(),
    pageSize: z.number(),
    totalCount: z.number(),
    totalPages: z.number(),
    hasNext: z.boolean(),
    hasPrevious: z.boolean(),
  }),
});
export type MemberListResponse = z.infer<typeof memberListResponseSchema>;

/** User's organization membership */
export const userMembershipSchema = z.object({
  organization: organizationResponseSchema,
  role: RoleKeySchema,
  joinedAt: z.string().datetime(),
});
export type UserMembership = z.infer<typeof userMembershipSchema>;

/** User's organizations list response */
export const userOrganizationsResponseSchema = z.object({
  data: z.array(userMembershipSchema),
});
export type UserOrganizationsResponse = z.infer<typeof userOrganizationsResponseSchema>;
